import React, { useEffect, useMemo, useState } from 'react';
import { ButtonDropdown } from 'framework/form';
import { useFilterContext } from '../../FilterContext';
import isEqual from 'lodash/isEqual';
import { getActiveFilters } from '../../useFilter';
import classnames from 'classnames';
import FilterIcon from '@mui/icons-material/FilterListOutlined';
import { Box, Typography } from '@mui/material';

const QuickFiltersLayout = ({
  children,
  displayedValue,
}: {
  children?: React.ReactElement[];
  displayedValue?: string;
}) => {
  const { filters = {} } = useFilterContext();
  const [isMenuShown, setIsMenuShown] = useState(false);

  const toggleMenu = () => setIsMenuShown(!isMenuShown);

  const value = useMemo(() => {
    const activeFilters = getActiveFilters(filters);
    const activeValue = React.Children.toArray(children).find(({ props: { state = {} } = {} }: any) =>
      isEqual(activeFilters, state),
    ) as React.ReactElement;

    if (activeValue) {
      return activeValue?.props.label;
    } else {
      return '';
    }
  }, [children, filters]);

  const filterValue = displayedValue || value;

  useEffect(() => {
    setIsMenuShown(false);
  }, [value, displayedValue]);

  return children ? (
    <Box
      className={classnames('quick-filter-button', { applied: !!filterValue })}
      sx={theme => ({
        display: 'inline-flex',
        marginRight: '27px',
        marginBottom: '17px',
        marginTop: '-10px',
        '&:not(:last-of-type)::after': {
          content: '""',
          display: 'inline-flex',
          width: '1px',
          minHeight: '21px',
          marginBottom: '10px',
          backgroundColor: 'border.default',
          transform: 'translate(13px, 10px)',
        },
        '& > button': { transform: 'translateY(4.5px)' },
        [theme.breakpoints.down('sm')]: {
          marginTop: 0,
          '&:not(:last-of-type)::after': { transform: 'translate(13px, 12px)' },
          '& > button': { transform: 'translateY(6.5px)' },
        },
        '& .button-dropdown__toggle': {
          color: 'primary.main',
          padding: '2px 10px 2px 7px',
          opacity: 1,
          '& > svg:last-of-type': { display: 'none' },
        },
        '&.applied .button-dropdown__toggle': {
          color: 'primary.main',
          backgroundColor: 'background.primary',
          borderColor: 'background.primary',
          '& svg': { fill: 'primary.main' },
        },
      })}
    >
      <ButtonDropdown
        label={
          <>
            <FilterIcon sx={{ mr: '8px', fontSize: '20px' }} />
            <Typography component="span" variant="inherit">
              Quick Filters{filterValue ? ':' : ''}
            </Typography>
          </>
        }
        displayedValue={filterValue}
        toggleMenu={toggleMenu}
        isOpen={isMenuShown}
        className="quick-filter-popper"
      >
        {React.Children.map(children, child =>
          React.cloneElement(child, {
            className: classnames(child.props.className, 'button-dropdown__menu-item'),
          }),
        )}
      </ButtonDropdown>
    </Box>
  ) : null;
};

export default QuickFiltersLayout;
